import React, { useState } from 'react';
import { Container } from 'react-bootstrap';
import Header from '../common/Header';
import Sidebar from '../common/Sidebar';

interface DashboardLayoutProps {
  children: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: string;
  actions?: React.ReactNode;
  stickyHeader?: React.ReactNode;
  scrollable?: boolean;
}

const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';

const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
  title,
  subtitle,
  actions,
  stickyHeader,
  scrollable,
}) => {
  // Initialize from localStorage
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return stored === 'true';
  });

  const sidebarWidth = isSidebarCollapsed ? '70px' : '280px';

  const handleToggleSidebar = () => {
    const newState = !isSidebarCollapsed;
    setIsSidebarCollapsed(newState);
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(newState));
  };

  // SSE connection status notification removed - now handled by global BackendStatusNotification
  // which shows overall backend health via circuit breaker state

  return (
    <div className="d-flex flex-column" style={{ height: '100vh', overflow: 'hidden' }}>
      <Header />

      <div className="d-flex flex-grow-1" style={{ minHeight: 0 }}>
        <div className="d-none d-lg-block">
          <Sidebar isCollapsed={isSidebarCollapsed} onToggle={handleToggleSidebar} />
        </div>

        <div
          className="flex-grow-1 d-flex flex-column bg-body-tertiary"
          style={{ minHeight: 0, ...(scrollable ? { overflowY: 'auto' as const } : {}) }}
        >
          {/* Sticky header section - inside main content area */}
          {stickyHeader && (
            <div
              className="bg-body-tertiary border-bottom p-3 flex-shrink-0"
              style={{
                zIndex: 1020,
              }}
            >
              <Container fluid>
                {(title || subtitle || actions) && (
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      {title && (
                        <h1 className="h3 mb-1 text-body fw-bold">{title}</h1>
                      )}
                      {subtitle && (
                        <p className="text-muted mb-0">{subtitle}</p>
                      )}
                    </div>
                    {actions && (
                      <div className="d-flex gap-2">
                        {actions}
                      </div>
                    )}
                  </div>
                )}
                {stickyHeader}
              </Container>
            </div>
          )}

          <Container
            fluid
            className="p-4 d-flex flex-column flex-grow-1"
            style={{
              width: `calc(100vw - ${sidebarWidth})`,
              transition: 'width 0.3s ease',
              minHeight: scrollable ? '240px' : 0,
            }}
          >
            {((title || subtitle || actions) && !stickyHeader) && (
              <div className="d-flex justify-content-between align-items-start mb-4 flex-shrink-0">
                <div>
                  {title && (
                    <h1 className="h3 mb-1 text-body fw-bold">{title}</h1>
                  )}
                  {subtitle && (
                    <p className="text-muted mb-0">{subtitle}</p>
                  )}
                </div>
                {actions && (
                  <div className="d-flex gap-2">
                    {actions}
                  </div>
                )}
              </div>
            )}

            <div className="flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
              {children}
            </div>
          </Container>
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
